// React related imports
import { FC, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';

// Components
import { Dropzone, Input, TagsField } from '@GxT-Team/gxt-components';
import { InputError } from '../../components/inputError';
import useModal from '../../hooks/useModal';
import CatalogItemSlideOver from '../../components/CatalogItemSlideOver';

// Types
import { ITemplateFields } from './types';
import { ItemType } from '../../services/api/types';
import { SourceFileGroup } from '../upload';
// API calls
import { getAllCatalogItems } from '../settings/components/utils/apiHelpers';

// utils
import { generateTagsFieldOptions } from './utils/helpers';
import { notifyError, notifySuccess } from '../../helpers/utils';
import { useOfficeService } from '../../services/api/officeService/useOfficeService';
import TemplateValidation from './components/templateValidation';
import { TemplateVariablesTable } from './components/templateVariableTable';
import { convertDocxToHTML } from '../../helpers/convertFileType';

const TemplateFields: FC<ITemplateFields> = ({
  control,
  errors,
  setValue,
  external,
  files,
  setFiles,
  setHtmlContent,
  setTemplateVariables,
  templateVariables,
}) => {
  const catalogItemSlideOver = useModal();
  const [selectedRegions, setSelectedRegions] = useState<SourceFileGroup[]>([]);
  const [selectedCTDSections, setSelectedCTDSections] = useState<SourceFileGroup[]>([]);
  const [isRegionModal, setIsRegionModal] = useState<boolean>(true);
  const [isConvertingToHtml, setIsConvertingToHtml] = useState(false);

  const MAX_FILES = 1;

  // Catalog items query
  const { data: catalogItems, isLoading: isCatalogLoading } = useQuery({
    queryKey: [isRegionModal ? ItemType.Region : ItemType.CTDSection],
    queryFn: () => getAllCatalogItems(isRegionModal ? ItemType.Region : ItemType.CTDSection, true),
  });

  const { templateAndProjectFields } = useOfficeService();
  const isLoadingTemplateVars = templateAndProjectFields.isPending;
  const { mutateAsync: mutateTemplateFields } = templateAndProjectFields;

  const handleCloseCreateItem = () => {
    catalogItemSlideOver.closeModal();
  };

  const handleOpenRegionModal = () => {
    setIsRegionModal(true);
    catalogItemSlideOver.openModal();
  };
  const handleResetSelection = () =>
    isRegionModal ? setSelectedRegions([]) : setSelectedCTDSections([]);

  const handleOpenCTDSectionsModal = () => {
    setIsRegionModal(false);
    catalogItemSlideOver.openModal();
  };

  useEffect(() => {
    setValue('regions', selectedRegions, { shouldValidate: true, shouldDirty: true });
  }, [selectedRegions, setValue]);

  useEffect(() => {
    setValue('ctdSections', selectedCTDSections, { shouldValidate: true, shouldDirty: true });
  }, [selectedCTDSections, setValue]);

  useEffect(() => {
    if (files.length === 0) return;

    let isMounted = true;
    const documentFile = files[0];

    const processTemplateUpload = async () => {
      try {
        setIsConvertingToHtml(true);
        const htmlConversionResult = await convertDocxToHTML(documentFile);
        if (isMounted) {
          setHtmlContent(
            typeof htmlConversionResult.value === 'string' ? htmlConversionResult.value : '',
          );
        }
      } catch (err) {
        console.error('Error converting document to HTML:', err);
        if (isMounted) {
          setHtmlContent('');
        }
        notifyError('Failed to convert template to HTML');
      } finally {
        if (isMounted) {
          setIsConvertingToHtml(false);
        }
      }

      const templateAndProjectFieldsFormData = new FormData();
      templateAndProjectFieldsFormData.append('documentFile', documentFile);

      try {
        const [templateVarsRes, projectFieldsRes] = await Promise.all([
          mutateTemplateFields({
            data: templateAndProjectFieldsFormData,
            queryParams: 'type=template_variable',
          }),
          mutateTemplateFields({
            data: templateAndProjectFieldsFormData,
            queryParams: 'type=project_field',
          }),
        ]);

        if (isMounted) {
          setTemplateVariables({
            templateVariables: Array.isArray(templateVarsRes.data) ? templateVarsRes.data : [],
            projectPlaceholders: Array.isArray(projectFieldsRes.data) ? projectFieldsRes.data : [],
          });
        }
      } catch (err) {
        console.error('Error fetching template fields:', err);
        notifyError('Failed to fetch template variables');
      }
    };

    processTemplateUpload();

    return () => {
      isMounted = false;
    };
  }, [files, mutateTemplateFields, setHtmlContent, setTemplateVariables]);

  const handleOnDrop = (newFiles: File[]) => {
    if (newFiles.length > MAX_FILES) {
      notifyError(`You can upload a maximum of ${MAX_FILES} files`);
      return;
    }
    const totalFiles = newFiles.length + files.length;
    if (totalFiles > MAX_FILES) {
      notifyError(`You can upload a maximum of ${MAX_FILES} files`);
      setFiles((prev) => [...prev, ...newFiles].slice(0, MAX_FILES));
    } else {
      setFiles((prev) => [...prev, ...newFiles]);
    }

    if (newFiles.length > 0) {
      const fileName = newFiles[0].name;
      const nameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
      setValue('templateName', nameWithoutExtension, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  // Update the copyToClipboard function
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        notifySuccess(`Copied to clipboard: ${text}`);
      },
      (err) => {
        console.error('Could not copy text: ', err);
        notifyError('Failed to copy to clipboard');
      },
    );
  };

  // Function to copy all variables at once
  const copyAllToClipboard = (data: string[]) => {
    if (!data || data.length === 0) return;

    const allText = data.join('\n');
    navigator.clipboard.writeText(allText).then(
      () => {
        notifySuccess(`Copied ${data.length} variables to clipboard`);
      },
      (err) => {
        console.error('Could not copy text: ', err);
        notifyError('Failed to copy to clipboard');
      },
    );
  };

  // Update the TemplateVariablesTable component

  return (
    <div className="flex flex-col items-start gap-4 w-2/5 self-stretch">
      <p className="text-lg font-bold leading-6 tracking-tighter text-primary-300">
        {` ${external ? 'External Document' : 'Template'} information`}
      </p>
      <div className="flex flex-col items-start gap-1 w-full">
        <Controller
          name="templateName"
          control={control}
          render={({ field }) => (
            <div className="w-full">
              <Input
                {...field}
                inputSize={'small'}
                label={`${external ? 'External' : ''} Document name*`}
                primary
                placeholder="Document name"
                customStyles={{
                  container: { width: '100%' },
                  input: { borderRadius: '0.375rem' },
                  label: { color: 'var(--Primary-300, #115873)' },
                }}
              />
              <InputError errors={errors} field={'templateName'} />
            </div>
          )}
        />
      </div>
      <div className="flex flex-col items-start gap-1 w-full">
        <Controller
          name="ctdSections"
          control={control}
          render={() => {
            const ctdSectionListOptions = generateTagsFieldOptions(selectedCTDSections);
            return (
              <div className="w-full" onClick={handleOpenCTDSectionsModal}>
                <TagsField
                  value={ctdSectionListOptions}
                  onChange={() => {
                    /* Synced via setValue when the slide-over saves */
                  }}
                  onClick={handleOpenCTDSectionsModal}
                  label="CTD Section*"
                />
                <InputError errors={errors} field={'ctdSections'} />
              </div>
            );
          }}
        />
      </div>
      <div className="flex items-start gap-4 self-stretch">
        <Controller
          name="version"
          control={control}
          render={({ field }) => (
            <div className="w-1/2">
              <Input
                {...field}
                inputType={'number'}
                min={0}
                step={0.1}
                inputSize={'small'}
                label={'Version*'}
                primary
                placeholder="Version"
                customStyles={{
                  container: { marginRight: '0.5rem' },
                  input: { borderRadius: '0.375rem' },
                  label: { color: 'var(--primary-300, #115873)' },
                }}
              />
              <InputError errors={errors} field={'version'} />
            </div>
          )}
        />
        <Controller
          name="regions"
          control={control}
          render={() => {
            const regionListOptions = generateTagsFieldOptions(selectedRegions);
            return (
              <div className="w-full" onClick={handleOpenRegionModal}>
                <TagsField
                  value={regionListOptions}
                  onChange={() => {
                    /* Synced via setValue when the slide-over saves */
                  }}
                  onClick={handleOpenRegionModal}
                  label="Region*"
                />
                <InputError errors={errors} field={'regions'} />
              </div>
            );
          }}
        />
        {
          // TODO: This dropdown will be used for Section in future
          // <Dropdown
          //   customStyles={{
          //     button: { width: '100%', height: '36px' },
          //     container: { flex: 1 },
          //     placeholder: { justifyContent: 'flex-start' },
          //     label: { color: 'var(--Primary-300, #115873)', fontWeight: '700' },
          //   }}
          //   dropdownIcon
          //   placeholder="Section"
          //   label="Section*"
          //   onSelect={() => {}}
          //   options={['Option 1', 'Option 2', 'Option 3 is a very big option']}
          //   position="bottom"
          //   type="select"
          // />
        }
      </div>
      {catalogItemSlideOver.show && (
        <CatalogItemSlideOver
          onClose={handleCloseCreateItem}
          isOpen={catalogItemSlideOver.show}
          heading={isRegionModal ? 'Select Regions' : 'Select CTD Sections'}
          catalogItems={catalogItems || []}
          selectedItems={isRegionModal ? selectedRegions : selectedCTDSections}
          handleAdd={isRegionModal ? setSelectedRegions : setSelectedCTDSections}
          handleResetSelection={handleResetSelection}
          isSingleSelect={!isRegionModal}
          isLoading={isCatalogLoading}
        />
      )}
      <div className="w-full h-[4.32rem]">
        <p className="text-primary-300 font-medium">Upload Template *</p>
        {files.length ? (
          <ul className="mt-2 space-y-1">
            {files.map((file, index) => (
              <li key={index} className="text-sm text-neutral-600">
                {file.name}
              </li>
            ))}
          </ul>
        ) : (
          <Dropzone maxFiles={MAX_FILES} onDrop={handleOnDrop} label="Drag and drop here" />
        )}
      </div>
      <p className="mt-4 text-primary-200 text-xs not-italic font-normal leading-[1.125rem] tracking-[0.00088rem]">
        Items marked with a * are required
      </p>
      <TemplateValidation files={files} convertToHtml={{ isPending: isConvertingToHtml }} />
      {(templateVariables.templateVariables.length > 0 ||
        templateVariables.projectPlaceholders.length > 0 ||
        isLoadingTemplateVars) && (
        <div className="w-full mt-4">
          <div className="border border-neutral-200 rounded-md p-4 bg-white">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-primary-300 font-bold text-base">Variables</h2>
              {isLoadingTemplateVars && (
                <div className="flex items-center">
                  <svg
                    className="animate-spin h-4 w-4 text-primary-300 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm text-primary-300">Loading variables...</span>
                </div>
              )}
            </div>

            {templateVariables.templateVariables.length > 0 && (
              <TemplateVariablesTable
                data={templateVariables.templateVariables}
                title="Template Variables"
                copyToClipboard={copyToClipboard}
                copyAllToClipboard={copyAllToClipboard}
              />
            )}

            {templateVariables.projectPlaceholders.length > 0 && (
              <TemplateVariablesTable
                data={templateVariables.projectPlaceholders}
                title="Project Placeholders"
                copyToClipboard={copyToClipboard}
                copyAllToClipboard={copyAllToClipboard}
              />
            )}

            {!isLoadingTemplateVars &&
              templateVariables.templateVariables.length === 0 &&
              templateVariables.projectPlaceholders.length === 0 && (
                <p className="text-neutral-500 text-sm italic">
                  No template variables found in document
                </p>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateFields;
